import { dueTimeOf } from '@nedelnik/shared';

import type { Task } from '@nedelnik/shared';

/**
 * Срок задачи — в Google Календарь (3.6.7, этап 22 плана).
 *
 * Ссылка на форму «новое событие» с заполненными полями. Собирается здесь, в
 * клиенте: сервер не участвует, заголовок не покидает устройство — это
 * совместимо со сквозным шифрованием (этап 18). В одну сторону: правка срока в
 * календарь не уезжает, и защиты от дублей у ссылки нет — форма о прежних
 * событиях не знает, и каждое нажатие даёт новое.
 *
 * Файла `.ics` здесь больше нет: он скачивался в загрузки, где выглядел
 * подозрительно, а на iPhone из установленного приложения уходил в окно
 * «Поделиться» без пункта «Календарь». Владелец попросил убрать скачивание
 * везде (2026-09-19). Один календарь вместо всех — осознанно: у Google есть
 * ссылка на форму, у остальных нет ничего, кроме файла.
 *
 * Описание задачи в событие не кладётся: оно длинное и часто личное, а в
 * календаре нужны заголовок и время. Открыть форму сразу на вкладке «Задача»
 * нельзя: у Google Tasks нет ссылки с заполненными полями, только API с
 * авторизацией.
 */

/** Столько длится встреча, у которой известно только начало. */
const DEFAULT_DURATION_MINUTES = 60;

type CalendarTask = Pick<Task, 'title' | 'due'> & { due_time?: string | null };

function pad(value: number): string {
  return value < 10 ? `0${String(value)}` : String(value);
}

function localDate(date: Date): string {
  return `${String(date.getFullYear())}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
}

function localStamp(date: Date): string {
  return `${localDate(date)}T${pad(date.getHours())}${pad(date.getMinutes())}00`;
}

/**
 * Когда событие: `начало/конец` в форме Google. Время — настенное, без зоны,
 * как и в задаче (3.6.2): без `Z` Google подставит пояс календаря
 * пользователя и покажет 17:00 как 17:00. Без времени — на весь день, и конец
 * у такого события исключающий: следующая дата.
 */
function datesOf(task: CalendarTask): string | null {
  if (task.due === null) return null;

  const [year, month, day] = task.due.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return null;
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;

  const time = dueTimeOf(task);
  if (time === null) {
    return `${localDate(new Date(year, month - 1, day))}/${localDate(new Date(year, month - 1, day + 1))}`;
  }

  const [hours, minutes] = time.split(':').map(Number);
  if (hours === undefined || minutes === undefined) return null;
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;

  const start = new Date(year, month - 1, day, hours, minutes);
  const end = new Date(start.getTime() + DEFAULT_DURATION_MINUTES * 60_000);
  return `${localStamp(start)}/${localStamp(end)}`;
}

export function googleCalendarUrl(task: CalendarTask): string | null {
  const dates = datesOf(task);
  if (dates === null) return null;

  const params = new URLSearchParams({ action: 'TEMPLATE', text: task.title ?? '', dates });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
